import useLockScroll from '@/common/hooks/useLockScroll';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

/** Below this, a navigation feels instant and a full-screen loader is only a flash. */
export const SLOW_NAVIGATION_OVERLAY_DELAY_MS = 400;

/**
 * True while a client navigation to a matching URL is in flight *and* has taken longer than
 * `delayMs`. With data caching and search keep-alive (docs/performance.md §۳.۲, §۳.۵) most
 * search ↔ profile transitions finish well under that, so the overlay no longer flashes;
 * it only shows up on a genuinely slow network/render. (The top progress bar was removed.)
 */
export const useSlowNavigationOverlay = (
  matches: (url: string) => boolean,
  delayMs = SLOW_NAVIGATION_OVERLAY_DELAY_MS,
) => {
  const router = useRouter();
  const { lockScroll, openScroll } = useLockScroll();
  const [visible, setVisible] = useState(false);
  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let shown = false;

    const stop = () => {
      clearTimeout(timer);
      timer = undefined;
      if (shown) {
        shown = false;
        setVisible(false);
        openScroll();
      }
    };

    const start = (url: string) => {
      stop();
      if (!matchesRef.current(url)) return;
      timer = setTimeout(() => {
        shown = true;
        setVisible(true);
        lockScroll();
      }, delayMs);
    };

    router.events.on('routeChangeStart', start);
    router.events.on('routeChangeComplete', stop);
    router.events.on('routeChangeError', stop);
    return () => {
      router.events.off('routeChangeStart', start);
      router.events.off('routeChangeComplete', stop);
      router.events.off('routeChangeError', stop);
      stop();
    };
  }, [delayMs]);

  return visible;
};
